/**
 * IndexedDB utility for caching data with timestamps
 *
 * This module provides persistent caching using IndexedDB to:
 * - Store fetched data across sessions
 * - Track lastModified timestamps to determine if re-fetch is needed
 * - Reduce network requests and improve performance
 */

import { appName } from "@getdevpulse/settings/browser"
import { logger } from "./logger"

/**
 * Generate database name based on the configured app name.
 * This allows different forks to have their own isolated cache.
 */
function getDBName(): string {
  const name = appName?.trim() || "default"
  return `${name}-cache`
}

const DB_NAME = getDBName()
const DB_VERSION = 1
const STORE_NAME = "data-cache"

export interface CachedItem<T = unknown> {
  url: string
  data: T
  lastModified: number // Unix timestamp in milliseconds
  cachedAt: number // When we cached this data
}

/**
 * Initialize IndexedDB database
 */
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) {
      reject(new Error("IndexedDB not supported"))
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      logger.error("Failed to open IndexedDB", request.error, {
        component: "indexeddb",
      })
      reject(request.error)
    }

    request.onsuccess = () => {
      resolve(request.result)
    }

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, {
          keyPath: "url",
        })
        objectStore.createIndex("lastModified", "lastModified", {
          unique: false,
        })
        objectStore.createIndex("cachedAt", "cachedAt", { unique: false })
        logger.info("IndexedDB object store created", {
          component: "indexeddb",
        })
      }
    }
  })
}

/**
 * Get cached data by URL
 */
export async function getCached<T = unknown>(url: string): Promise<CachedItem<T> | null> {
  try {
    const db = await openDatabase()
    const transaction = db.transaction([STORE_NAME], "readonly")
    const store = transaction.objectStore(STORE_NAME)
    const request = store.get(url)

    return new Promise((resolve, reject) => {
      request.onerror = () => {
        logger.error(`Failed to get cached data for ${url}`, request.error, {
          component: "indexeddb",
        })
        reject(request.error)
      }

      request.onsuccess = () => {
        const result = request.result as CachedItem<T> | undefined
        if (result) {
          logger.debug(`Cache hit for ${url}`, {
            component: "indexeddb",
            lastModified: new Date(result.lastModified).toISOString(),
            cachedAt: new Date(result.cachedAt).toISOString(),
          })
        }
        resolve(result || null)
      }
    })
  } catch (error) {
    logger.error(`Error accessing cache for ${url}`, error, {
      component: "indexeddb",
    })
    return null
  }
}

/**
 * Store data in cache with timestamp
 */
export async function setCached<T = unknown>(
  url: string,
  data: T,
  lastModified: number,
): Promise<void> {
  try {
    const db = await openDatabase()
    const transaction = db.transaction([STORE_NAME], "readwrite")
    const store = transaction.objectStore(STORE_NAME)

    const item: CachedItem<T> = {
      url,
      data,
      lastModified,
      cachedAt: Date.now(),
    }

    const request = store.put(item)

    return new Promise((resolve, reject) => {
      request.onerror = () => {
        logger.error(`Failed to cache data for ${url}`, request.error, {
          component: "indexeddb",
        })
        reject(request.error)
      }

      request.onsuccess = () => {
        logger.debug(`Cached data for ${url}`, {
          component: "indexeddb",
          lastModified: new Date(lastModified).toISOString(),
        })
        resolve()
      }
    })
  } catch (error) {
    logger.error(`Error caching data for ${url}`, error, {
      component: "indexeddb",
    })
    throw error
  }
}

/**
 * Check if cached data is still valid based on lastModified timestamp
 *
 * @param url - The URL to check
 * @param serverLastModified - The lastModified timestamp from the server
 * @returns true if cache is valid, false if needs refresh
 */
export async function isCacheValid(url: string, serverLastModified: number): Promise<boolean> {
  const cached = await getCached(url)

  if (!cached) {
    logger.debug(`No cache found for ${url}`, { component: "indexeddb" })
    return false
  }

  const isValid = cached.lastModified >= serverLastModified

  if (!isValid) {
    logger.debug(`Cache expired for ${url}`, {
      component: "indexeddb",
      cachedLastModified: new Date(cached.lastModified).toISOString(),
      serverLastModified: new Date(serverLastModified).toISOString(),
    })
  }

  return isValid
}

/**
 * Clear cache for a specific URL or all cache
 */
export async function clearCache(url?: string): Promise<void> {
  try {
    const db = await openDatabase()
    const transaction = db.transaction([STORE_NAME], "readwrite")
    const store = transaction.objectStore(STORE_NAME)

    if (url) {
      const request = store.delete(url)
      await new Promise<void>((resolve, reject) => {
        request.onerror = () => reject(request.error)
        request.onsuccess = () => {
          logger.debug(`Cleared cache for ${url}`, {
            component: "indexeddb",
          })
          resolve()
        }
      })
    } else {
      const request = store.clear()
      await new Promise<void>((resolve, reject) => {
        request.onerror = () => reject(request.error)
        request.onsuccess = () => {
          logger.info("Cleared all cache", { component: "indexeddb" })
          resolve()
        }
      })
    }
  } catch (error) {
    logger.error("Error clearing cache", error, { component: "indexeddb" })
    throw error
  }
}

/**
 * Get cache statistics
 */
export async function getCacheStats(): Promise<{
  count: number
  totalSize: number
  oldestEntry: Date | null
  newestEntry: Date | null
}> {
  try {
    const db = await openDatabase()
    const transaction = db.transaction([STORE_NAME], "readonly")
    const store = transaction.objectStore(STORE_NAME)
    const request = store.getAll()

    return new Promise((resolve, reject) => {
      request.onerror = () => reject(request.error)

      request.onsuccess = () => {
        const items = request.result as CachedItem[]
        let totalSize = 0
        let oldestEntry: Date | null = null
        let newestEntry: Date | null = null

        for (const item of items) {
          // Rough size estimation
          totalSize += JSON.stringify(item.data).length

          const cachedDate = new Date(item.cachedAt)
          if (!oldestEntry || cachedDate < oldestEntry) {
            oldestEntry = cachedDate
          }
          if (!newestEntry || cachedDate > newestEntry) {
            newestEntry = cachedDate
          }
        }

        resolve({
          count: items.length,
          totalSize,
          oldestEntry,
          newestEntry,
        })
      }
    })
  } catch (error) {
    logger.error("Error getting cache stats", error, {
      component: "indexeddb",
    })
    return {
      count: 0,
      totalSize: 0,
      oldestEntry: null,
      newestEntry: null,
    }
  }
}

/**
 * Check if IndexedDB is available
 */
export function isIndexedDBAvailable(): boolean {
  return "indexedDB" in window
}
